import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { dashboardAPI, classesAPI, termsAPI, roomsAPI, teachersAPI, schedulesAPI, bookingsAPI, waitlistAPI, invoicesAPI, discountsAPI, paymentsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Toaster, toast } from 'sonner';
import { 
  LayoutDashboard, Users, BookOpen, Calendar, MapPin, PoundSterling, 
  BarChart3, Mail, Clock, CreditCard, Tag, UserPlus, Receipt, Send, FileText, AlertTriangle, CheckCircle2, RefreshCw, Shield, Download, Ticket, MessageSquare
} from 'lucide-react';
import Navbar from '../components/Navbar';
import { formatPrice, DAYS_OF_WEEK, CATEGORIES } from '../lib/utils';
import { computeUkBusinessTaxes, generateBreakdownText } from '../lib/vatTaxUtils';
import api from '../lib/api';

// Import refactored tab components
import { 
  OverviewTab, 
  ClassesTab, 
  FinanceTab, 
  DiscountsTab,
  SchedulesTab,
  RoomsTab,
  TermsTab,
  WaitlistTab,
  BookingsTab,
  TrialsTab,
  ContactSubmissionsTab,
  PageContentTab,
  DataCleanupTab
} from '../components/admin';
import EmailTemplatesTab from '../components/admin/EmailTemplatesTab';
import MailingLists from './MailingLists';
import EmailLogsTab from '../components/admin/EmailLogsTab';
import TrialCommsTab from '../components/admin/TrialCommsTab';
import VATDashboard from '../components/admin/VATDashboard';
import DataIntegrityTab from '../components/admin/DataIntegrityTab';
import DataExportTab from '../components/admin/DataExportTab';
import PendingPaymentsTab from '../components/admin/PendingPaymentsTab';
import RenewalsTab from '../components/admin/RenewalsTab';
import RegistrationProgressTab from '../components/admin/RegistrationProgressTab';
import NoShowsTab from '../components/admin/NoShowsTab';
import CreditBundlesTab from '../components/admin/CreditBundlesTab';
import FbLeadsTab from '../components/admin/FbLeadsTab';
import CollegeAccessTab from '../components/admin/CollegeAccessTab';
import AdminSEO from '../components/common/AdminSEO';

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Core data state
  const [stats, setStats] = useState(null);
  const [classes, setClasses] = useState([]);
  const [terms, setTerms] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingEmails, setPendingEmails] = useState({ reminders: [], followups: [] });
  const [sendingEmail, setSendingEmail] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  
  // Expenses editing state
  const [editingExpenses, setEditingExpenses] = useState(false);
  const [expensesInput, setExpensesInput] = useState('');
  const [savingExpenses, setSavingExpenses] = useState(false);
  
  // Waitlist state
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [notifyingId, setNotifyingId] = useState(null);
  
  // Trials state
  const [trials, setTrials] = useState([]);
  const [trialsLoading, setTrialsLoading] = useState(false);
  const [sendingTrialEmail, setSendingTrialEmail] = useState(null);
  
  // Modal states
  const [classModalOpen, setClassModalOpen] = useState(false);
  const [termModalOpen, setTermModalOpen] = useState(false);
  const [roomModalOpen, setRoomModalOpen] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [uploadingClassPhoto, setUploadingClassPhoto] = useState(false);
  
  // Finance tab states
  const [latePayments, setLatePayments] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [loadingFinance, setLoadingFinance] = useState(false);
  const [pendingBookings, setPendingBookings] = useState([]);
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [authorizingPayments, setAuthorizingPayments] = useState(false);
  const [refundModal, setRefundModal] = useState(null);
  const [processingRefund, setProcessingRefund] = useState(false);
  
  // Top Clients state
  const [topClients, setTopClients] = useState([]);
  const [loadingTopClients, setLoadingTopClients] = useState(false);
  
  // Discounts state
  const [discounts, setDiscounts] = useState([]);
  const [bootcamps, setBootcamps] = useState([]);
  const [discountModalOpen, setDiscountModalOpen] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [discountForm, setDiscountForm] = useState({
    name: '', description: '', discount_type: 'percentage', value: 10,
    applies_to: 'all', code: '', is_active: true,
    max_uses: '', valid_from: '', valid_until: '',
    class_ids: [], bootcamp_ids: [], student_ids: []
  });

  // Form states
  const [classForm, setClassForm] = useState({
    name: '', description: '', category: 'ballet', age_min: 5, age_max: 18,
    capacity: 15, six_week_price: null, term_price: null, one_off_price: null, subscription_price: null,
    teacher_id: '', room_id: '', duration_minutes: 60, image_url: '', teacher_ids: [],
    is_subscription: false, is_visible: true, address: '',
    day_of_week: '', start_time: '', end_time: '', term_dates: '', offers_free_trial: true
  });
  const [termForm, setTermForm] = useState({ name: '', start_date: '', end_date: '', is_active: true });
  const [roomForm, setRoomForm] = useState({ name: '', capacity: 20, description: '' });
  const [scheduleForm, setScheduleForm] = useState({
    class_id: '', term_id: '', room_id: '', day_of_week: 0, start_time: '09:00', end_time: '10:00'
  });

  // Determine active tab from URL
  const activeTab = location.pathname.includes('/students') ? 'students'
    : location.pathname.includes('/trials') ? 'trials'
    : location.pathname.includes('/no-shows') ? 'no-shows'
    : location.pathname.includes('/classes') ? 'classes'
    : location.pathname.includes('/schedules') ? 'schedules'
    : location.pathname.includes('/terms') ? 'terms'
    : location.pathname.includes('/rooms') ? 'rooms'
    : location.pathname.includes('/bookings') ? 'bookings'
    : location.pathname.includes('/waitlist') ? 'waitlist'
    : location.pathname.includes('/finance') ? 'finance'
    : location.pathname.includes('/pending-payments') ? 'pending-payments'
    : location.pathname.includes('/renewals') ? 'renewals'
    : location.pathname.includes('/credit-bundles') ? 'credit-bundles'
    : location.pathname.includes('/registration-progress') ? 'registration-progress'
    : location.pathname.includes('/discounts') ? 'discounts'
    : location.pathname.includes('/email-templates') ? 'email-templates'
    : location.pathname.includes('/trial-comms') ? 'trial-comms'
    : location.pathname.includes('/email-logs') ? 'email-logs'
    : location.pathname.includes('/mailing-lists') ? 'mailing-lists'
    : location.pathname.includes('/vat') ? 'vat'
    : location.pathname.includes('/contact') ? 'contact'
    : location.pathname.includes('/fb-leads') ? 'fb-leads'
    : location.pathname.includes('/college') ? 'college'
    : location.pathname.includes('/pages') ? 'pages'
    : location.pathname.includes('/data-cleanup') ? 'data-cleanup'
    : location.pathname.includes('/data-integrity') ? 'data-integrity'
    : location.pathname.includes('/data-export') ? 'data-export'
    : 'overview';

  // Compute UK VAT & Corporation Tax breakdown
  const taxBreakdown = useMemo(() => {
    return computeUkBusinessTaxes({ 
      totalSales: stats?.total_revenue,
      expenses: stats?.total_expenses ?? 0
    });
  }, [stats?.total_revenue, stats?.total_expenses]);

  const copyTaxBreakdown = () => {
    const text = generateBreakdownText(taxBreakdown);
    navigator.clipboard.writeText(text)
      .then(() => toast.success('Breakdown copied to clipboard'))
      .catch(() => toast.error('Failed to copy'));
  };

  // Data fetching - split into critical (initial render) and non-critical (can load after)
  useEffect(() => {
    // Critical data needed for initial render
    fetchData();
  }, []);
  
  useEffect(() => {
    // Non-critical data - load after initial render
    if (!loading) {
      fetchPendingEmails();
      fetchTopClients();
      fetchStripeStatus();
    }
  }, [loading]);

  useEffect(() => {
    if (activeTab === 'waitlist') fetchWaitlist();
    if (activeTab === 'finance') fetchFinanceData();
    if (activeTab === 'discounts') fetchDiscounts();
    if (activeTab === 'trials') fetchTrials();
    if (activeTab === 'no-shows') fetchTrials();
  }, [activeTab]);

  const fetchData = async () => {
    try {
      // Fetch each endpoint individually to identify failures
      let statsRes, classesRes, termsRes, roomsRes, teachersRes, schedulesRes, bookingsRes;
      
      try {
        statsRes = await dashboardAPI.getStats();
      } catch (e) {
        console.error('Failed to fetch stats:', e);
        throw new Error('Stats API failed');
      }
      
      try {
        classesRes = await classesAPI.getAllForAdmin();
      } catch (e) {
        console.error('Failed to fetch classes:', e);
        throw new Error('Classes API failed');
      }
      
      try {
        termsRes = await termsAPI.getAll();
      } catch (e) {
        console.error('Failed to fetch terms:', e);
        throw new Error('Terms API failed');
      }
      
      try {
        roomsRes = await roomsAPI.getAll();
      } catch (e) {
        console.error('Failed to fetch rooms:', e);
        throw new Error('Rooms API failed');
      }
      
      try {
        teachersRes = await teachersAPI.getAll();
      } catch (e) {
        console.error('Failed to fetch teachers:', e);
        throw new Error('Teachers API failed');
      }
      
      try {
        schedulesRes = await schedulesAPI.getAllForAdmin();
      } catch (e) {
        console.error('Failed to fetch schedules:', e);
        throw new Error('Schedules API failed');
      }
      
      try {
        bookingsRes = await bookingsAPI.getGrouped();
      } catch (e) {
        console.error('Failed to fetch bookings:', e);
        throw new Error('Bookings API failed');
      }

      setStats(statsRes.data);
      setClasses(classesRes.data);
      setTerms(termsRes.data);
      setRooms(roomsRes.data);
      setTeachers(teachersRes.data);
      setSchedules(schedulesRes.data);
      setBookings(bookingsRes.data);
    } catch (error) {
      console.error('Dashboard data fetch error:', error);
      toast.error(`Failed to load dashboard data: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingEmails = async () => {
    try {
      const res = await api.get('/dashboard/pending-emails');
      setPendingEmails(res.data);
    } catch (error) {
      console.error('Failed to fetch pending emails:', error);
    }
  };

  const fetchStripeStatus = async () => {
    try {
      const res = await paymentsAPI.getStripeStatus();
      setStripeStatus(res.data);
    } catch (error) {
      console.error('Failed to fetch Stripe status:', error);
    }
  };

  const fetchTopClients = async () => {
    setLoadingTopClients(true);
    try {
      const res = await dashboardAPI.getTopClients(10);
      setTopClients(res.data);
    } catch (error) {
      console.error('Failed to fetch top clients:', error);
    } finally {
      setLoadingTopClients(false);
    }
  };

  const fetchWaitlist = async () => {
    setWaitlistLoading(true);
    try {
      const res = await waitlistAPI.getAll();
      setWaitlistEntries(res.data);
    } catch (error) {
      console.error('Failed to fetch waitlist:', error);
    } finally {
      setWaitlistLoading(false);
    }
  };

  const fetchTrials = async () => {
    setTrialsLoading(true);
    try {
      const res = await api.get('/free-trials');
      setTrials(res.data);
    } catch (error) {
      console.error('Failed to fetch trials:', error);
    } finally {
      setTrialsLoading(false);
    }
  };

  const handleSendTrialReminder = async (trialId) => {
    setSendingTrialEmail(`reminder-${trialId}`);
    try {
      await api.post(`/free-trials/${trialId}/send-reminder`);
      toast.success('Reminder email sent!');
      fetchTrials();
    } catch (error) {
      toast.error('Failed to send reminder');
    } finally {
      setSendingTrialEmail(null);
    }
  };

  const handleSendTrialFollowup = async (trialId) => {
    setSendingTrialEmail(`followup-${trialId}`);
    try {
      await api.post(`/free-trials/${trialId}/send-followup`);
      toast.success('Follow-up email sent!');
      fetchTrials();
    } catch (error) {
      toast.error('Failed to send follow-up');
    } finally {
      setSendingTrialEmail(null);
    }
  };

  const fetchFinanceData = async () => {
    setLoadingFinance(true);
    try {
      const [lateRes, invoicesRes] = await Promise.all([
        invoicesAPI.getLatePayments(),
        invoicesAPI.getAll()
      ]);
      setLatePayments(lateRes.data);
      setAllInvoices(invoicesRes.data);
      // Pending bookings are filtered from all invoices with pending status
      const pending = invoicesRes.data.filter(inv => inv.status === 'pending');
      setPendingBookings(pending);
    } catch (error) {
      console.error('Failed to fetch finance data:', error);
    } finally {
      setLoadingFinance(false);
    }
  };

  const fetchDiscounts = async () => {
    try {
      const res = await discountsAPI.getAll();
      setDiscounts(res.data);
    } catch (error) {
      console.error('Failed to fetch discounts:', error);
    }
    // Also load bootcamps so the discount modal can scope to specific camps.
    try {
      const apiBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
      const token = localStorage.getItem('token');
      const r = await fetch(`${apiBase}/api/bootcamps?include_past=true`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (r.ok) {
        const data = await r.json();
        setBootcamps(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch bootcamps:', error);
    }
  };

  // Expenses handlers
  const handleEditExpenses = () => {
    setExpensesInput(stats?.total_expenses?.toString() || '0');
    setEditingExpenses(true);
  };

  const handleSaveExpenses = async () => {
    const value = parseFloat(expensesInput) || 0;
    if (value < 0) {
      toast.error('Expenses cannot be negative');
      return;
    }
    setSavingExpenses(true);
    try {
      await dashboardAPI.updateExpenses(value);
      setStats(prev => ({ ...prev, total_expenses: value }));
      setEditingExpenses(false);
      toast.success('Expenses updated');
    } catch (error) {
      toast.error('Failed to update expenses');
    } finally {
      setSavingExpenses(false);
    }
  };

  // Class handlers
  const handleClassSubmit = async () => {
    try {
      if (editingItem) {
        await classesAPI.update(editingItem.id, classForm);
        toast.success('Class updated');
      } else {
        await classesAPI.create(classForm);
        toast.success('Class created');
      }
      setClassModalOpen(false);
      setEditingItem(null);
      resetClassForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDeleteClass = async (id) => {
    if (!confirm('Delete this class?')) return;
    try {
      await classesAPI.delete(id);
      toast.success('Class deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete class');
    }
  };

  const handleReorderClass = async (classId, direction) => {
    const currentIndex = classes.findIndex(c => c.id === classId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= classes.length) return;
    
    // Create new order
    const newClasses = [...classes];
    const [removed] = newClasses.splice(currentIndex, 1);
    newClasses.splice(newIndex, 0, removed);
    
    // Update sort_order for affected classes
    try {
      const updates = newClasses.map((cls, idx) => 
        classesAPI.update(cls.id, { sort_order: idx })
      );
      await Promise.all(updates);
      
      // Update local state immediately for responsiveness
      setClasses(newClasses.map((cls, idx) => ({ ...cls, sort_order: idx })));
      toast.success('Class order updated');
    } catch (error) {
      toast.error('Failed to reorder classes');
      fetchData(); // Refresh to get correct order
    }
  };

  const editClass = (cls) => {
    setEditingItem(cls);
    setClassForm({ ...cls });
    setClassModalOpen(true);
  };

  const resetClassForm = () => {
    setClassForm({
      name: '', description: '', category: 'ballet', age_min: 5, age_max: 18,
      capacity: 15, six_week_price: null, term_price: null, one_off_price: null, subscription_price: null,
      teacher_id: '', room_id: '', duration_minutes: 60, image_url: '', teacher_ids: [],
      is_subscription: false, is_visible: true, address: '',
      day_of_week: '', start_time: '', end_time: '', term_dates: '', offers_free_trial: true
    });
  };

  const handleClassPhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be under 5MB');
      return;
    }
    setUploadingClassPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Use persistent upload for class photos (stores in MongoDB for permanence)
      const res = await api.post('/site-media/upload/persistent', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Use the data_url directly for storage (base64 encoded image)
      const imageUrl = res.data.data_url || res.data.url;
      setClassForm(prev => ({ ...prev, image_url: imageUrl }));
      toast.success('Photo uploaded permanently!');
    } catch (err) {
      // Fallback to regular upload if persistent fails (file too large for MongoDB)
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post('/site-media/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setClassForm(prev => ({ ...prev, image_url: res.data.url }));
        toast.warning('Photo uploaded but may not persist. For permanent storage, use images under 2MB.');
      } catch (fallbackErr) {
        toast.error('Failed to upload photo');
      }
    } finally {
      setUploadingClassPhoto(false);
    }
  };

  const toggleTeacherAssignment = (teacherId, role = 'lead') => {
    setClassForm(prev => {
      const existing = prev.teacher_ids?.find(t => t.teacher_id === teacherId);
      if (existing) {
        return { ...prev, teacher_ids: prev.teacher_ids.filter(t => t.teacher_id !== teacherId) };
      }
      return { ...prev, teacher_ids: [...(prev.teacher_ids || []), { teacher_id: teacherId, role }] };
    });
  };

  const updateTeacherRole = (teacherId, newRole) => {
    setClassForm(prev => ({
      ...prev,
      teacher_ids: prev.teacher_ids.map(t => 
        t.teacher_id === teacherId ? { ...t, role: newRole } : t
      )
    }));
  };

  // Term handlers
  const handleTermSubmit = async () => {
    try {
      if (editingItem) {
        await termsAPI.update(editingItem.id, termForm);
        toast.success('Term updated');
      } else {
        await termsAPI.create(termForm);
        toast.success('Term created');
      }
      setTermModalOpen(false);
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to save term');
    }
  };

  const handleDeleteTerm = async (id) => {
    if (!confirm('Delete this term?')) return;
    try {
      await termsAPI.delete(id);
      toast.success('Term deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete term');
    }
  };

  const editTerm = (term) => {
    setEditingItem(term);
    setTermForm({ ...term });
    setTermModalOpen(true);
  };

  // Room handlers
  const handleRoomSubmit = async () => {
    try {
      if (editingItem) {
        await roomsAPI.update(editingItem.id, roomForm);
        toast.success('Room updated');
      } else {
        await roomsAPI.create(roomForm);
        toast.success('Room created');
      }
      setRoomModalOpen(false);
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to save room');
    }
  };

  const handleDeleteRoom = async (id) => {
    if (!confirm('Delete this room?')) return;
    try {
      await roomsAPI.delete(id);
      toast.success('Room deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete room');
    }
  };

  const editRoom = (room) => {
    setEditingItem(room);
    setRoomForm({ ...room });
    setRoomModalOpen(true);
  };

  // Schedule handlers
  const handleScheduleSubmit = async () => {
    try {
      await schedulesAPI.create(scheduleForm);
      toast.success('Schedule created');
      setScheduleModalOpen(false);
      setScheduleForm({
        class_id: '', term_id: '', room_id: '', day_of_week: 0, start_time: '09:00', end_time: '10:00'
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create schedule');
    }
  };

  const handleDeleteSchedule = async (id) => {
    if (!confirm('Delete this schedule?')) return;
    try {
      await schedulesAPI.delete(id);
      toast.success('Schedule deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete schedule');
    }
  };

  // Waitlist handlers
  const notifyWaitlistParent = async (id) => {
    setNotifyingId(id);
    try {
      await waitlistAPI.notify(id);
      toast.success('Parent notified!');
      fetchWaitlist();
    } catch (error) {
      toast.error('Failed to notify parent');
    } finally {
      setNotifyingId(null);
    }
  };

  const removeFromWaitlist = async (id) => {
    if (!confirm('Remove from waitlist?')) return;
    try {
      await waitlistAPI.remove(id);
      toast.success('Removed from waitlist');
      fetchWaitlist();
    } catch (error) {
      toast.error('Failed to remove from waitlist');
    }
  };

  // Finance handlers
  const handleAuthorizePayments = async () => {
    if (selectedBookings.length === 0) return;
    setAuthorizingPayments(true);
    try {
      await Promise.all(selectedBookings.map(id => bookingsAPI.authorizePayment(id)));
      toast.success(`${selectedBookings.length} payment(s) authorized`);
      setSelectedBookings([]);
      fetchFinanceData();
    } catch (error) {
      toast.error('Failed to authorize payments');
    } finally {
      setAuthorizingPayments(false);
    }
  };

  const handleProcessRefund = async (refundData) => {
    setProcessingRefund(true);
    try {
      await invoicesAPI.refund(refundData.id, {
        amount: refundData.refund_amount || refundData.amount,
        reason: refundData.reason
      });
      toast.success('Refund processed');
      setRefundModal(null);
      fetchFinanceData();
    } catch (error) {
      toast.error('Failed to process refund');
    } finally {
      setProcessingRefund(false);
    }
  };

  // Discount handlers
  const handleCreateDiscount = async () => {
    try {
      const data = {
        ...discountForm,
        max_uses: discountForm.max_uses ? parseInt(discountForm.max_uses) : null,
        valid_from: discountForm.valid_from || null,
        valid_until: discountForm.valid_until || null
      };
      if (editingDiscount) {
        await discountsAPI.update(editingDiscount.id, data);
        toast.success('Discount updated');
      } else {
        await discountsAPI.create(data);
        toast.success('Discount created');
      }
      setDiscountModalOpen(false);
      setEditingDiscount(null);
      fetchDiscounts();
    } catch (error) {
      toast.error('Failed to save discount');
    }
  };

  const handleDeleteDiscount = async (id) => {
    if (!confirm('Delete this discount?')) return;
    try {
      await discountsAPI.delete(id);
      toast.success('Discount deleted');
      fetchDiscounts();
    } catch (error) {
      toast.error('Failed to delete discount');
    }
  };

  // Email handlers
  const handleSendEmail = async (type, email) => {
    setSendingEmail(`${type}-${email.booking_id || email.id}`);
    try {
      if (type === 'reminder') {
        await api.post(`/free-trials/${email.id}/send-reminder`);
      } else {
        await api.post(`/free-trials/${email.id}/send-followup`);
      }
      toast.success('Email sent!');
      fetchPendingEmails();
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setSendingEmail(null);
    }
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <AdminSEO title="Access Denied" />
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Access Denied</h1>
          <p className="text-white/60 mb-4">You need admin privileges to access this page.</p>
          <Button onClick={() => navigate('/login')}>Login</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] pb-20 md:pb-8">
      <AdminSEO title="Admin Dashboard" />
      <Navbar />
      <Toaster position="top-right" richColors />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 bg-gradient-to-br from-[#FF007F] to-[#7000FF] rounded-xl flex items-center justify-center">
            <LayoutDashboard className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-white">Admin Dashboard</h1>
            <p className="text-white/60">Manage your performing arts school</p>
          </div>
        </div>

        {/* Stripe Status Banner */}
        {stripeStatus && !stripeStatus.is_live_mode && (
          <div className={`p-4 rounded-lg border ${stripeStatus.is_test_mode ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
            <div className="flex items-center gap-3">
              <AlertTriangle className={`w-5 h-5 ${stripeStatus.is_test_mode ? 'text-yellow-400' : 'text-red-400'}`} />
              <div className="flex-1">
                <p className={`font-medium ${stripeStatus.is_test_mode ? 'text-yellow-400' : 'text-red-400'}`}>
                  {stripeStatus.is_test_mode ? 'Stripe Test Mode Active' : 'Stripe Not Configured'}
                </p>
                <p className="text-sm text-white/60">
                  {stripeStatus.is_test_mode 
                    ? 'Payments will not process real money. To accept real payments, update your STRIPE_API_KEY in the backend .env file with your live key (sk_live_...).'
                    : 'Stripe is not properly configured. Add your STRIPE_API_KEY to the backend .env file to enable payments.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} className="space-y-6">
          <TabsList className="bg-[#111] border border-[#333] p-1 flex-wrap h-auto gap-1">
            <TabsTrigger value="overview" onClick={() => navigate('/admin')} className="data-[state=active]:bg-[#FF007F] data-[state=active]:text-white" data-testid="tab-overview">
              <BarChart3 className="w-4 h-4 mr-2" />Overview
            </TabsTrigger>
            <TabsTrigger value="students" onClick={() => navigate('/admin/students')} className="data-[state=active]:bg-[#FF007F] data-[state=active]:text-white" data-testid="tab-students">
              <Users className="w-4 h-4 mr-2" />Students
            </TabsTrigger>
            <TabsTrigger value="trials" onClick={() => navigate('/admin/trials')} className="data-[state=active]:bg-cyan-500 data-[state=active]:text-white" data-testid="tab-trials">
              <UserPlus className="w-4 h-4 mr-2" />Trials
            </TabsTrigger>
            <TabsTrigger value="no-shows" onClick={() => navigate('/admin/no-shows')} className="data-[state=active]:bg-red-600 data-[state=active]:text-white" data-testid="tab-no-shows">
              <AlertTriangle className="w-4 h-4 mr-2" />No-Shows
            </TabsTrigger>
            <TabsTrigger value="classes" onClick={() => navigate('/admin/classes')} className="data-[state=active]:bg-[#FF007F] data-[state=active]:text-white" data-testid="tab-classes">
              <BookOpen className="w-4 h-4 mr-2" />Classes
            </TabsTrigger>
            <TabsTrigger value="schedules" onClick={() => navigate('/admin/schedules')} className="data-[state=active]:bg-[#FF007F] data-[state=active]:text-white" data-testid="tab-schedules">
              <Calendar className="w-4 h-4 mr-2" />Schedules
            </TabsTrigger>
            <TabsTrigger value="terms" onClick={() => navigate('/admin/terms')} className="data-[state=active]:bg-[#FF007F] data-[state=active]:text-white" data-testid="tab-terms">
              <Calendar className="w-4 h-4 mr-2" />Terms
            </TabsTrigger>
            <TabsTrigger value="rooms" onClick={() => navigate('/admin/rooms')} className="data-[state=active]:bg-[#FF007F] data-[state=active]:text-white" data-testid="tab-rooms">
              <MapPin className="w-4 h-4 mr-2" />Rooms
            </TabsTrigger>
            <TabsTrigger value="bookings" onClick={() => navigate('/admin/bookings')} className="data-[state=active]:bg-[#FF007F] data-[state=active]:text-white" data-testid="tab-bookings">
              <Calendar className="w-4 h-4 mr-2" />Bookings
            </TabsTrigger>
            <TabsTrigger value="waitlist" onClick={() => navigate('/admin/waitlist')} className="data-[state=active]:bg-[#7000FF] data-[state=active]:text-white" data-testid="tab-waitlist">
              <Clock className="w-4 h-4 mr-2" />Waitlist
            </TabsTrigger>
            <TabsTrigger value="finance" onClick={() => navigate('/admin/finance')} className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="tab-finance">
              <CreditCard className="w-4 h-4 mr-2" />Finance
            </TabsTrigger>
            <TabsTrigger value="pending-payments" onClick={() => navigate('/admin/pending-payments')} className="data-[state=active]:bg-amber-500 data-[state=active]:text-white" data-testid="tab-pending-payments">
              <PoundSterling className="w-4 h-4 mr-2" />Take Payments
            </TabsTrigger>
            <TabsTrigger value="renewals" onClick={() => navigate('/admin/renewals')} className="data-[state=active]:bg-cyan-500 data-[state=active]:text-white" data-testid="tab-renewals">
              <RefreshCw className="w-4 h-4 mr-2" />Renewals
            </TabsTrigger>
            <TabsTrigger value="credit-bundles" onClick={() => navigate('/admin/credit-bundles')} className="data-[state=active]:bg-[#FF007F] data-[state=active]:text-white" data-testid="tab-credit-bundles">
              <Ticket className="w-4 h-4 mr-2" />PAYG Credits
            </TabsTrigger>
            <TabsTrigger value="registration-progress" onClick={() => navigate('/admin/registration-progress')} className="data-[state=active]:bg-teal-500 data-[state=active]:text-white" data-testid="tab-registration-progress">
              <Users className="w-4 h-4 mr-2" />Progress
            </TabsTrigger>
            <TabsTrigger value="reports" onClick={() => navigate('/admin/reports')} className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black" data-testid="tab-reports">
              <PoundSterling className="w-4 h-4 mr-2" />Reports
            </TabsTrigger>
            <TabsTrigger value="discounts" onClick={() => navigate('/admin/discounts')} className="data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-discounts">
              <Tag className="w-4 h-4 mr-2" />Discounts
            </TabsTrigger>
            <TabsTrigger value="email-templates" onClick={() => navigate('/admin/email-templates')} className="data-[state=active]:bg-purple-600 data-[state=active]:text-white" data-testid="tab-email-templates">
              <Mail className="w-4 h-4 mr-2" />Emails
            </TabsTrigger>
            <TabsTrigger value="trial-comms" onClick={() => navigate('/admin/trial-comms')} className="data-[state=active]:bg-[#25D366] data-[state=active]:text-black" data-testid="tab-trial-comms">
              <Mail className="w-4 h-4 mr-2" />Trial Comms
            </TabsTrigger>
            <TabsTrigger value="mailing-lists" onClick={() => navigate('/admin/mailing-lists')} className="data-[state=active]:bg-sky-500 data-[state=active]:text-white" data-testid="tab-mailing-lists">
              <Mail className="w-4 h-4 mr-2" />Mailing Lists
            </TabsTrigger>
            <TabsTrigger value="email-logs" onClick={() => navigate('/admin/email-logs')} className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white" data-testid="tab-email-logs">
              <Send className="w-4 h-4 mr-2" />Logs
            </TabsTrigger>
            <TabsTrigger value="vat" onClick={() => navigate('/admin/vat')} className="data-[state=active]:bg-red-600 data-[state=active]:text-white" data-testid="tab-vat">
              <Receipt className="w-4 h-4 mr-2" />VAT
            </TabsTrigger>
            <TabsTrigger value="contact" onClick={() => navigate('/admin/contact')} className="data-[state=active]:bg-pink-600 data-[state=active]:text-white" data-testid="tab-contact">
              <Mail className="w-4 h-4 mr-2" />Enquiries
            </TabsTrigger>
            <TabsTrigger value="fb-leads" onClick={() => navigate('/admin/fb-leads')} className="data-[state=active]:bg-[#1877F2] data-[state=active]:text-white" data-testid="tab-fb-leads">
              <MessageSquare className="w-4 h-4 mr-2" />FB Leads
            </TabsTrigger>
            <TabsTrigger value="college" onClick={() => navigate('/admin/college')} className="data-[state=active]:bg-[#7000FF] data-[state=active]:text-white" data-testid="tab-college">
              <Shield className="w-4 h-4 mr-2" />College
            </TabsTrigger>
            <TabsTrigger value="pages" onClick={() => navigate('/admin/pages')} className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white" data-testid="tab-pages">
              <FileText className="w-4 h-4 mr-2" />Pages
            </TabsTrigger>
            <TabsTrigger value="data-cleanup" onClick={() => navigate('/admin/data-cleanup')} className="data-[state=active]:bg-yellow-600 data-[state=active]:text-white" data-testid="tab-data-cleanup">
              <AlertTriangle className="w-4 h-4 mr-2" />Cleanup
            </TabsTrigger>
            <TabsTrigger value="data-integrity" onClick={() => navigate('/admin/data-integrity')} className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white" data-testid="tab-data-integrity">
              <Shield className="w-4 h-4 mr-2" />Integrity
            </TabsTrigger>
            <TabsTrigger value="data-export" onClick={() => navigate('/admin/data-export')} className="data-[state=active]:bg-orange-600 data-[state=active]:text-white" data-testid="tab-data-export">
              <Download className="w-4 h-4 mr-2" />Export
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {loading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[1,2,3,4].map(i => <div key={i} className="h-32 bg-[#111] rounded-xl animate-pulse" />)}
              </div>
            ) : (
              <OverviewTab
                stats={stats}
                taxBreakdown={taxBreakdown}
                topClients={topClients}
                loadingTopClients={loadingTopClients}
                pendingEmails={pendingEmails}
                editingExpenses={editingExpenses}
                setEditingExpenses={setEditingExpenses}
                expensesInput={expensesInput}
                setExpensesInput={setExpensesInput}
                savingExpenses={savingExpenses}
                handleEditExpenses={handleEditExpenses}
                handleSaveExpenses={handleSaveExpenses}
                copyTaxBreakdown={copyTaxBreakdown}
                sendingEmail={sendingEmail}
                handleSendEmail={handleSendEmail}
                setClassModalOpen={setClassModalOpen}
                setScheduleModalOpen={setScheduleModalOpen}
                setRoomModalOpen={setRoomModalOpen}
                setTermModalOpen={setTermModalOpen}
                navigate={navigate}
              />
            )}
          </TabsContent>

          {/* Classes Tab */}
          <TabsContent value="classes">
            <ClassesTab
              classes={classes}
              teachers={teachers}
              rooms={rooms}
              classForm={classForm}
              setClassForm={setClassForm}
              classModalOpen={classModalOpen}
              setClassModalOpen={setClassModalOpen}
              editingItem={editingItem}
              setEditingItem={setEditingItem}
              uploadingClassPhoto={uploadingClassPhoto}
              handleClassPhotoUpload={handleClassPhotoUpload}
              handleClassSubmit={handleClassSubmit}
              handleDeleteClass={handleDeleteClass}
              handleReorderClass={handleReorderClass}
              editClass={editClass}
              resetClassForm={resetClassForm}
              toggleTeacherAssignment={toggleTeacherAssignment}
              updateTeacherRole={updateTeacherRole}
            />
          </TabsContent>

          {/* Schedules Tab */}
          <TabsContent value="schedules">
            <SchedulesTab
              schedules={schedules}
              classes={classes}
              terms={terms}
              rooms={rooms}
              scheduleForm={scheduleForm}
              setScheduleForm={setScheduleForm}
              scheduleModalOpen={scheduleModalOpen}
              setScheduleModalOpen={setScheduleModalOpen}
              handleScheduleSubmit={handleScheduleSubmit}
              handleDeleteSchedule={handleDeleteSchedule}
            />
          </TabsContent>

          {/* Terms Tab */}
          <TabsContent value="terms">
            <TermsTab
              terms={terms}
              termForm={termForm}
              setTermForm={setTermForm}
              termModalOpen={termModalOpen}
              setTermModalOpen={setTermModalOpen}
              editingItem={editingItem}
              setEditingItem={setEditingItem}
              handleTermSubmit={handleTermSubmit}
              handleDeleteTerm={handleDeleteTerm}
              editTerm={editTerm}
            />
          </TabsContent>

          {/* Rooms Tab */}
          <TabsContent value="rooms">
            <RoomsTab
              rooms={rooms}
              roomForm={roomForm}
              setRoomForm={setRoomForm}
              roomModalOpen={roomModalOpen}
              setRoomModalOpen={setRoomModalOpen}
              editingItem={editingItem}
              setEditingItem={setEditingItem}
              handleRoomSubmit={handleRoomSubmit}
              handleDeleteRoom={handleDeleteRoom}
              editRoom={editRoom}
            />
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <BookingsTab
              bookings={bookings}
              navigate={navigate}
              onRefresh={fetchData}
            />
          </TabsContent>

          {/* Waitlist Tab */}
          <TabsContent value="waitlist">
            <WaitlistTab
              waitlistEntries={waitlistEntries}
              waitlistLoading={waitlistLoading}
              notifyingId={notifyingId}
              fetchWaitlist={fetchWaitlist}
              notifyWaitlistParent={notifyWaitlistParent}
              removeFromWaitlist={removeFromWaitlist}
            />
          </TabsContent>

          {/* Trials Tab */}
          <TabsContent value="trials">
            <TrialsTab
              trials={trials}
              trialsLoading={trialsLoading}
              sendingTrialEmail={sendingTrialEmail}
              fetchTrials={fetchTrials}
              handleSendTrialReminder={handleSendTrialReminder}
              handleSendTrialFollowup={handleSendTrialFollowup}
            />
          </TabsContent>

          {/* No-Shows Tab */}
          <TabsContent value="no-shows">
            <NoShowsTab
              trials={trials}
              trialsLoading={trialsLoading}
              fetchTrials={fetchTrials}
            />
          </TabsContent>

          {/* Finance Tab */}
          <TabsContent value="finance">
            <FinanceTab />
          </TabsContent>

          {/* Pending Payments Tab */}
          <TabsContent value="pending-payments">
            <PendingPaymentsTab />
          </TabsContent>

          {/* Renewals Tab */}
          <TabsContent value="renewals">
            <RenewalsTab />
          </TabsContent>

          {/* PAYG Credit Bundles Tab */}
          <TabsContent value="credit-bundles">
            <CreditBundlesTab />
          </TabsContent>

          {/* Registration Progress Tab */}
          <TabsContent value="registration-progress">
            <RegistrationProgressTab />
          </TabsContent>

          {/* Discounts Tab */}
          <TabsContent value="discounts">
            <DiscountsTab
              discounts={discounts}
              classes={classes}
              bootcamps={bootcamps}
              discountForm={discountForm}
              setDiscountForm={setDiscountForm}
              discountModalOpen={discountModalOpen}
              setDiscountModalOpen={setDiscountModalOpen}
              editingDiscount={editingDiscount}
              setEditingDiscount={setEditingDiscount}
              handleCreateDiscount={handleCreateDiscount}
              handleDeleteDiscount={handleDeleteDiscount}
            />
          </TabsContent>

          {/* Email Templates Tab */}
          <TabsContent value="email-templates">
            <EmailTemplatesTab />
          </TabsContent>

          <TabsContent value="trial-comms">
            <TrialCommsTab />
          </TabsContent>

          {/* Email Logs Tab */}
          <TabsContent value="email-logs">
            <EmailLogsTab />
          </TabsContent>

          {/* Mailing Lists Tab */}
          <TabsContent value="mailing-lists">
            <MailingLists embedded={true} />
          </TabsContent>

          {/* VAT Threshold Tab */}
          <TabsContent value="vat">
            <VATDashboard />
          </TabsContent>

          {/* Contact Submissions Tab */}
          <TabsContent value="contact">
            <ContactSubmissionsTab />
          </TabsContent>

          {/* Facebook Leads Tab */}
          <TabsContent value="fb-leads">
            <FbLeadsTab />
          </TabsContent>

          {/* College Access Tab */}
          <TabsContent value="college">
            <CollegeAccessTab />
          </TabsContent>

          {/* Page Content Editor Tab */}
          <TabsContent value="pages">
            <PageContentTab />
          </TabsContent>

          {/* Data Cleanup Tab */}
          <TabsContent value="data-cleanup">
            <DataCleanupTab />
          </TabsContent>

          <TabsContent value="data-integrity">
            <DataIntegrityTab />
          </TabsContent>

          <TabsContent value="data-export">
            <DataExportTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
